/**
 * Read-only compact summary of the built hand, shown before launching the
 * coach. Mirrors how the backend's [HAND REPLAY] reads it: a setup line, the
 * villains, then per-street board + action narrative, then how it ended. Pure
 * display — no editing.
 */

import type { BuiltHand, BuiltHandAction } from "@/shared/models";
import { PlayingCard } from "@/features/chat/components/PlayingCard";

function actionPhrase(a: BuiltHandAction): string {
  const amount = a.amount !== undefined ? ` to ${a.amount}bb` : "";
  switch (a.action) {
    case "check":
      return `${a.actor} checks`;
    case "fold":
      return `${a.actor} folds`;
    case "call":
      return `${a.actor} calls${a.amount !== undefined ? ` ${a.amount}bb` : ""}`;
    case "bet":
      return `${a.actor} bets${a.amount !== undefined ? ` ${a.amount}bb` : ""}`;
    case "raise":
      return `${a.actor} raises${amount}`;
    default:
      return `${a.actor} ${a.action}`;
  }
}

function ActionLine({ actions }: { actions: BuiltHandAction[] }) {
  if (actions.length === 0) {
    return <span className="text-text-muted/60">no actions</span>;
  }
  return <span>{actions.map(actionPhrase).join(", ")}.</span>;
}

function Cards({ tokens }: { tokens: (string | null)[] }) {
  return (
    <span className="inline-flex gap-1 align-middle">
      {tokens
        .filter((t): t is string => !!t)
        .map((t, i) => (
          <PlayingCard key={i} token={t} size="sm" />
        ))}
    </span>
  );
}

export function HandReplayPreview({ hand }: { hand: BuiltHand }) {
  const { flop, turn, river } = hand.streets;
  const endedLabel =
    hand.endedOn === "showdown"
      ? "went to showdown"
      : `ended on the ${hand.endedOn}`;

  return (
    <div className="rounded-2xl border border-border bg-surface p-4 space-y-3 text-sm">
      {/* Setup */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-text">Hero ({hand.heroPosition})</span>
        <Cards tokens={hand.heroCards} />
        <span className="text-text-muted">· {hand.effectiveStackBb}bb effective</span>
      </div>

      {/* Villains */}
      <div className="text-text-muted">
        vs{" "}
        {hand.villains.map((v, i) => (
          <span key={v.position} className="inline-flex items-center gap-1.5">
            {i > 0 && ", "}
            <span className="text-text">{v.position}</span>
            {v.cards && <Cards tokens={v.cards} />}
          </span>
        ))}
      </div>

      {/* Streets */}
      <div className="space-y-2 border-t border-border pt-3">
        <Street label="Preflop" actions={hand.streets.preflop.actions} />
        {flop && (
          <Street label="Flop" actions={flop.actions} board={flop.board} />
        )}
        {turn && <Street label="Turn" actions={turn.actions} board={[turn.card]} />}
        {river && (
          <Street label="River" actions={river.actions} board={[river.card]} />
        )}
      </div>

      <div className="border-t border-border pt-3 text-text-muted">
        The hand {endedLabel}.
      </div>
    </div>
  );
}

function Street({
  label,
  actions,
  board,
}: {
  label: string;
  actions: BuiltHandAction[];
  board?: (string | null)[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-14 shrink-0 text-xs uppercase tracking-wider text-text-muted">
        {label}
      </span>
      {board && board.some(Boolean) && <Cards tokens={board} />}
      <span className="text-text">
        <ActionLine actions={actions} />
      </span>
    </div>
  );
}
